import { useState, useContext } from 'react'
import { AuthContext } from '../../../context/AuthContext'
import { SessionContext } from '../../../context/SessionContext'
import { submitDemographicSurvey } from '../service/demographicSurveyService'

import '../css/post-survey.scss'
import '../css/demographic-survey.scss'

const CHI_STUDY_SESSIONS = [663, 664, 665, 666]   
const D2_SESSIONS = [669, 674, 675, 676, 677, 678]

const BASE_QUESTIONS = [
  {
    key: 'gender',
    label: 'What is your gender?',
    type: 'radio',
    options: ['Male', 'Female', 'Non-binary', 'Other', 'Prefer not to say'],
  },
  {
    key: 'year',
    label: 'What is your year in school?',
    type: 'radio',
    options: ['Freshman', 'Sophomore', 'Junior', 'Senior', 'Graduate'],
  },
  {
    key: 'major',
    label: 'What is your major?',
    type: 'text',
    placeholder: 'e.g., Computer Science',
  },
]

const CHI_QUESTIONS = [
  {
    key: 'vt_email',
    label: 'What is your VT email?',
    type: 'text',
    placeholder: 'e.g., pid@vt.edu',
    subtitle: 'Used to record extra credit and notify prize wheel winners. Your responses are kept confidential.',
  },
  ...BASE_QUESTIONS,
  {
    key: 'java_experience',
    label: 'How many years of experience do you have with Java?',
    type: 'radio',
    options: ['Less than 1 year', '1-2 years', '3-4 years', '5+ years'],
  },
  {
    key: 'data_structures_experience',
    label: 'How many years of experience do you have with data structures?',
    type: 'radio',
    options: ['Less than 1 year', '1-2 years', '3-4 years', '5+ years'],
  },
  {
    key: 'llm_coding_usage',
    label: 'How often do you use AI assistants (e.g., ChatGPT, Claude, Copilot) for coding?',
    type: 'radio',
    options: ['Never', 'Rarely', 'Weekly', 'Daily'],
  },
]

const DEFAULT_QUESTIONS = [
  ...BASE_QUESTIONS,
  {
    key: 'python_experience',
    label: 'What is your Python experience level?',
    type: 'radio',
    options: ['Beginner', 'Intermediate', 'Advanced'],
  },
  {
    key: 'llm_usage',
    label: 'How frequently do you use Large Language Models (e.g., ChatGPT, Claude, Gemini)?',
    type: 'radio',
    options: ['Never', 'Rarely', 'Weekly', 'Daily'],
  },
]

const VT_EMAIL_REGEX = /^[a-z0-9._%+-]+@vt\.edu$/

export default function DemographicSurvey({ onComplete }) {
  const { currentUser } = useContext(AuthContext)
  const { session } = useContext(SessionContext)

  const [responses, setResponses] = useState({})
  const [error, setError] = useState('')

  // D2 uses the same question set as the June deployment: email capture at
  // session start is the cross-deployment linkage key and the extra-credit
  // record, and the Java/DS experience items fit the CS 3114 population.
  const isCHI = CHI_STUDY_SESSIONS.includes(session?.id) || D2_SESSIONS.includes(session?.id)
  const questions = isCHI ? CHI_QUESTIONS : DEFAULT_QUESTIONS

  const handleSelect = (key, value) => {
    setResponses(prev => ({ ...prev, [key]: value }))
    setError('')
  }

  const handleSubmit = async () => {
    const allAnswered = questions.every(q => responses[q.key]?.toString().trim())
    if (!allAnswered) {
      setError('Please answer all questions before continuing.')
      return
    }

    // Validate + normalize the VT email. The normalized string is the
    // cross-deployment linkage key and the extra-credit record — malformed
    // or inconsistently-cased emails break both.
    const finalResponses = { ...responses }
    if (questions.some(q => q.key === 'vt_email')) {
      const email = (finalResponses.vt_email || '').trim().toLowerCase()
      if (!VT_EMAIL_REGEX.test(email)) {
        setError('Please enter a valid VT email address (ending in @vt.edu).')
        return
      }
      finalResponses.vt_email = email
    }

    try {
      await submitDemographicSurvey(currentUser.id, session.id, finalResponses)
      onComplete()
    } catch (err) {
      console.error('Failed to submit demographics:', err)
      onComplete()
    }
  }

  return (
    <div className="survey-overlay">
      <div className="survey-modal">
        <div className="survey-header">
          <h2>Quick Background Survey</h2>
          <p>Before you begin, please answer a few questions about yourself.</p>
        </div>

        <div className="survey-questions">
          {questions.map((question, index) => (
            <div className="survey-question" key={question.key}>
              <label>{index + 1}. {question.label}</label>
              {question.subtitle && (
                <span className="survey-subtitle">{question.subtitle}</span>
              )}

              {question.type === 'text' ? (
                <input
                  type="text"
                  className="survey-text-input"
                  placeholder={question.placeholder || ''}
                  value={responses[question.key] || ''}
                  onChange={(e) => handleSelect(question.key, e.target.value)}
                />
              ) : (
                <div className="survey-radio-group">
                  {question.options.map((option) => (
                    <button
                      key={option}
                      className={`survey-radio-option ${responses[question.key] === option ? 'selected' : ''}`}
                      onClick={() => handleSelect(question.key, option)}
                    >
                      {option}
                    </button>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>

        {error && <div className="survey-error">{error}</div>}

        <div className="survey-footer">
          <button className="survey-submit" onClick={handleSubmit}>
            Continue
          </button>
        </div>
      </div>
    </div>
  )
}